/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * Investor Routing Types
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Type definitions for the investor interaction routing system.
 * Enables deep-linking from notifications to actionable surfaces.
 *
 * @module lib/investor/routing/types
 */

import type { InvestorNotificationType } from '@/types/investorNotifications';

// ═══════════════════════════════════════════════════════════════════════════════
// PANEL TYPES
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Drawer panel variants available in the investor portal.
 * Each panel is a focused interaction surface for a specific entity type.
 */
export type InvestorPanelType =
    | 'position-detail'
    | 'milestone-review'
    | 'approval-review'
    | 'signature-review'
    | 'gate-review'
    | 'finance-review'
    | 'evidence-preview'
    | 'document-preview'
    | 'distribution-detail';

/**
 * Entity types that can be referenced by notifications.
 */
export type InvestorEntityType =
    | 'investment'
    | 'milestone'
    | 'distribution'
    | 'evidence_pack'
    | 'document'
    | 'approval_workflow'
    | 'signature_request'
    | 'gate'
    | 'finance_approval'
    | 'dataroom'
    | 'project';

// ═══════════════════════════════════════════════════════════════════════════════
// PANEL STATE
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * URL query parameter state for investor panel navigation.
 * Persisted in URL for deep-linking and browser history.
 */
export interface InvestorPanelState {
    /** Currently open panel type (null = no panel open) */
    panel: InvestorPanelType | null;
    /** Entity ID to display in the panel */
    entityId?: string;
    /** Entity type for context */
    entityType?: InvestorEntityType;
    /** Optional tab within the panel */
    tab?: string;
    /** Optional project context */
    projectId?: string;
    /** Position drawer tab, kept separate from collection-page filters. */
    positionTab?: 'overview' | 'records';
}

// ═══════════════════════════════════════════════════════════════════════════════
// NOTIFICATION TARGET
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Resolved navigation target from a notification.
 * Contains all information needed to route the user to the correct surface.
 */
export interface InvestorNotificationTarget {
    /** Page-local record view, separate from drawer query parameters. */
    hash?: string;
    /** Base route path (e.g., '/investor/milestones') */
    route: string;
    /** Query parameters for the route */
    queryParams: Record<string, string>;
    /** Panel to open (if any) */
    panel: InvestorPanelType | null;
    /** Entity ID referenced by the notification */
    entityId: string | null;
    /** Entity type for context */
    entityType: InvestorEntityType | null;
    /** Fallback route if entity is not found */
    fallbackRoute: string;
    /** Call-to-action label for the notification */
    ctaLabel: string;
    /** Whether this notification requires user action */
    actionRequired: boolean;
    /** Tab to open within the page (if applicable) */
    tab?: string;
}

// ═══════════════════════════════════════════════════════════════════════════════
// EVENT CATEGORY
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * High-level event categories for investor notifications.
 */
export type InvestorEventCategory =
    | 'milestones'
    | 'capital'
    | 'approvals'
    | 'signatures'
    | 'documents'
    | 'general';

/**
 * Mapping of notification types to their categories.
 */
export const NOTIFICATION_CATEGORY_MAP: Record<InvestorNotificationType, InvestorEventCategory> = {
    milestone_verified: 'milestones',
    distribution_paid: 'capital',
    distribution_pending: 'capital',
    evidence_available: 'documents',
    release_completed: 'capital',
    portfolio_update: 'general',
    alert: 'general',
    approval_requested: 'approvals',
    approval_decided: 'approvals',
    signature_requested: 'signatures',
    signature_completed: 'signatures',
    finance_approval_requested: 'capital',
    finance_approval_decided: 'capital',
    dataroom_access_granted: 'documents',
};

// ═══════════════════════════════════════════════════════════════════════════════
// ACTION REQUIREMENTS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Notification types that require investor action.
 */
export const ACTION_REQUIRED_TYPES: Set<InvestorNotificationType> = new Set([
    'milestone_verified',
    'evidence_available',
    'signature_requested',
    'finance_approval_requested',
]);

/**
 * Priority levels for notification routing (higher = more urgent).
 */
export const NOTIFICATION_PRIORITY: Record<InvestorNotificationType, number> = {
    signature_requested: 100,
    approval_requested: 90,
    finance_approval_requested: 85,
    alert: 80,
    distribution_pending: 70,
    milestone_verified: 60,
    evidence_available: 55,
    release_completed: 50,
    distribution_paid: 45,
    approval_decided: 40,
    signature_completed: 35,
    finance_approval_decided: 30,
    dataroom_access_granted: 20,
    portfolio_update: 10,
};
